/**
 * Notes API Service
 *
 * All calls go through the MSAL-authenticated apiClient, which auto-injects
 * the Bearer token from Microsoft Entra External ID.
 */

import { api } from '../auth/apiClient';

// ---------------------------------------------------------------------------
// CRUD
// ---------------------------------------------------------------------------

/** Create a new research note */
export async function createNote({ title, content, companyIds, tags, source, aiContext }) {
    return api.post('/notes', {
        title,
        content,
        companyIds,
        tags,
        source: source || 'manual',
        aiContext,
    });
}

/** Get a single note by ID */
export async function getNote(noteId) {
    return api.get(`/notes/${noteId}`);
}

/** List user's notes (paginated) */
export async function listNotes({ page = 1, limit = 20, sortBy = 'updatedAt', order = 'desc', companyId, tag } = {}) {
    const params = new URLSearchParams({ page, limit, sortBy, order });
    if (companyId) params.set('companyId', companyId);
    if (tag) params.set('tag', tag);
    return api.get(`/notes?${params.toString()}`);
}

/** Full update with version snapshot */
export async function updateNote(noteId, { title, content, companyIds, tags }) {
    return api.put(`/notes/${noteId}`, { title, content, companyIds, tags });
}

/** Lightweight auto-save (no version snapshot) */
export async function autosaveNote(noteId, { content, plainText, title }) {
    return api.patch(`/notes/${noteId}/autosave`, { content, plainText, title });
}

/** Soft-delete a note */
export async function deleteNote(noteId) {
    return api.delete(`/notes/${noteId}`);
}

// ---------------------------------------------------------------------------
// SEARCH
// ---------------------------------------------------------------------------

/** Full-text search across notes */
export async function searchNotes({ q, companyId, tag, dateFrom, dateTo, limit = 10 }) {
    const params = new URLSearchParams({ q, limit });
    if (companyId) params.set('companyId', companyId);
    if (tag) params.set('tag', tag);
    if (dateFrom) params.set('dateFrom', dateFrom);
    if (dateTo) params.set('dateTo', dateTo);
    return api.get(`/notes/search?${params.toString()}`);
}

// ---------------------------------------------------------------------------
// VERSIONS
// ---------------------------------------------------------------------------

/** Get version history for a note */
export async function getVersions(noteId, limit = 20) {
    return api.get(`/notes/${noteId}/versions?limit=${limit}`);
}

/** Restore a note to a previous version */
export async function restoreVersion(noteId, version) {
    return api.post(`/notes/${noteId}/versions`, { version });
}

// ---------------------------------------------------------------------------
// AI CONTEXT
// ---------------------------------------------------------------------------

/** Retrieve relevant notes for RAG context */
export async function getNotesContext({ query, companyIds, limit = 5 }) {
    return api.post('/notes/context', { query, companyIds, limit });
}
